// frontend/src/components/InterventionsList.js
import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Alert, Spinner, Badge, Card, Row, Col } from 'react-bootstrap';
import api from '../api';

const TYPES = ['travaux', 'inspection', 'livraison', 'reunion', 'autre'];
const STATUTS = ['planifiee', 'en_cours', 'terminee', 'annulee'];

const TYPE_BADGE = {
  travaux:    { bg: 'primary',   label: 'Travaux',    icon: '🔨' },
  inspection: { bg: 'warning',   label: 'Inspection', icon: '🔍' },
  livraison:  { bg: 'info',      label: 'Livraison',  icon: '🚚' },
  reunion:    { bg: 'secondary', label: 'Réunion',    icon: '👥' },
  autre:      { bg: 'dark',      label: 'Autre',      icon: '📌' },
};

const STATUT_BADGE = {
  planifiee: { bg: 'secondary', label: 'Planifiée' },
  en_cours:  { bg: 'primary',   label: 'En cours'  },
  terminee:  { bg: 'success',   label: 'Terminée'  },
  annulee:   { bg: 'danger',    label: 'Annulée'   },
};

const EMPTY_FORM = {
  chantier_id: '',
  user_id:     '',
  titre:       '',
  description: '',
  type:        'travaux',
  statut:      'planifiee',
  date_debut:  '',
  date_fin:    '',
  lieu:        '',
  notes:       ''
};

function InterventionsList({ onInterventionChange }) {
  const [interventions, setInterventions] = useState([]);
  const [chantiers, setChantiers]         = useState([]);
  const [users, setUsers]                 = useState([]);
  const [stats, setStats]                 = useState(null);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [showModal, setShowModal]         = useState(false);
  const [showConfirm, setShowConfirm]     = useState(false);
  const [editIntervention, setEditIntervention]         = useState(null);
  const [interventionToDelete, setInterventionToDelete] = useState(null);
  const [form, setForm]                   = useState(EMPTY_FORM);
  const [submitting, setSubmitting]       = useState(false);
  const [filtreStatut, setFiltreStatut]   = useState('');
  const [filtreType, setFiltreType]       = useState('');

  const fetchInterventions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (filtreStatut) params.append('statut', filtreStatut);
      if (filtreType)   params.append('type',   filtreType);
      const query = params.toString() ? `?${params.toString()}` : '';

      const [intRes, statsRes] = await Promise.all([
        api.get(`/interventions${query}`),
        api.get('/interventions/stats')
      ]);
      setInterventions(intRes.data);
      setStats(statsRes.data);
      if (onInterventionChange) onInterventionChange(intRes.data.length);
    } catch (err) {
      setError(`Erreur: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [filtreStatut, filtreType]);

  const fetchData = useCallback(async () => {
    try {
      const [chantiersRes, usersRes] = await Promise.all([
        api.get('/chantiers'),
        api.get('/users')
      ]);
      setChantiers(chantiersRes.data);
      setUsers(usersRes.data.filter(u => u.active));
    } catch (err) {
      console.error('Erreur chargement données:', err);
    }
  }, []);

  useEffect(() => { fetchInterventions(); }, [fetchInterventions]);
  useEffect(() => { fetchData(); },          [fetchData]);

  const handleField = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const openCreateModal = () => {
    setEditIntervention(null);
    setForm(EMPTY_FORM);
    setError(null);
    setShowModal(true);
  };

  const openEditModal = (i) => {
    setEditIntervention(i);
    setForm({
      chantier_id: i.chantier_id,
      user_id:     i.user_id     || '',
      titre:       i.titre,
      description: i.description || '',
      type:        i.type,
      statut:      i.statut,
      date_debut:  i.date_debut  ? i.date_debut.substring(0, 16) : '',
      date_fin:    i.date_fin    ? i.date_fin.substring(0, 16)   : '',
      lieu:        i.lieu        || '',
      notes:       i.notes       || ''
    });
    setError(null);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (editIntervention) {
        await api.put(`/interventions/${editIntervention.id}`, form);
      } else {
        await api.post('/interventions', form);
      }
      setShowModal(false);
      await fetchInterventions();
    } catch (err) {
      setError(`Erreur: ${err.response?.data?.error || err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/interventions/${interventionToDelete.id}`);
      setShowConfirm(false);
      setInterventionToDelete(null);
      await fetchInterventions();
    } catch (err) {
      setShowConfirm(false);
      setError(`Erreur: ${err.response?.data?.error || err.message}`);
    }
  };

  const formatDateTime = (val) => {
    if (!val) return '-';
    const d = new Date(val);
    return d.toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
        <p className="mt-2">Chargement des interventions...</p>
      </div>
    );
  }

  return (
    <>
      {error && (
        <Alert variant="danger" className="mt-3" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats */}
      {stats && (
        <Row className="mb-4 mt-3">
          <Col md={3}>
            <Card bg="secondary" text="white" className="text-center">
              <Card.Body>
                <Card.Title>📅 Planifiées</Card.Title>
                <h4>{stats.nb_planifiees}</h4>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card bg="primary" text="white" className="text-center">
              <Card.Body>
                <Card.Title>⚙️ En cours</Card.Title>
                <h4>{stats.nb_en_cours}</h4>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card bg="success" text="white" className="text-center">
              <Card.Body>
                <Card.Title>✅ Terminées</Card.Title>
                <h4>{stats.nb_terminees}</h4>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card bg="info" text="white" className="text-center">
              <Card.Body>
                <Card.Title>🔜 À venir</Card.Title>
                <h4>{stats.nb_a_venir}</h4>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Barre d'outils */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <h4 className="mb-0">Interventions ({interventions.length})</h4>
          <Form.Select size="sm" style={{ width: '150px' }}
            value={filtreStatut} onChange={(e) => setFiltreStatut(e.target.value)}>
            <option value="">Tous statuts</option>
            {STATUTS.map(s => (
              <option key={s} value={s}>{STATUT_BADGE[s].label}</option>
            ))}
          </Form.Select>
          <Form.Select size="sm" style={{ width: '150px' }}
            value={filtreType} onChange={(e) => setFiltreType(e.target.value)}>
            <option value="">Tous types</option>
            {TYPES.map(t => (
              <option key={t} value={t}>{TYPE_BADGE[t].icon} {TYPE_BADGE[t].label}</option>
            ))}
          </Form.Select>
        </div>
        <Button variant="primary" onClick={openCreateModal}>
          + Nouvelle Intervention
        </Button>
      </div>

      {interventions.length === 0 ? (
        <Alert variant="info">Aucune intervention pour le moment.</Alert>
      ) : (
        <Table striped bordered hover responsive>
          <thead className="table-dark">
            <tr>
              <th>N°</th>
              <th>Type</th>
              <th>Titre</th>
              <th>Chantier</th>
              <th>Responsable</th>
              <th>Statut</th>
              <th>Début</th>
              <th>Fin</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {interventions.map((i) => (
              <tr key={i.id}
                className={i.statut === 'en_cours' ? 'table-primary' :
                           i.statut === 'terminee' ? 'table-success' : ''}>
                <td>{i.id}</td>
                <td>
                  <Badge bg={TYPE_BADGE[i.type]?.bg || 'secondary'}>
                    {TYPE_BADGE[i.type]?.icon} {TYPE_BADGE[i.type]?.label || i.type}
                  </Badge>
                </td>
                <td>
                  <strong>{i.titre}</strong>
                  {i.lieu && <div><small className="text-muted">📍 {i.lieu}</small></div>}
                </td>
                <td>{i.chantier_nom || '-'}</td>
                <td>{i.responsable}</td>
                <td>
                  <Badge bg={STATUT_BADGE[i.statut]?.bg || 'secondary'}>
                    {STATUT_BADGE[i.statut]?.label || i.statut}
                  </Badge>
                </td>
                <td>{formatDateTime(i.date_debut)}</td>
                <td>{formatDateTime(i.date_fin)}</td>
                <td>
                  <div className="d-flex gap-1">
                    <Button size="sm" variant="outline-primary"
                      onClick={() => openEditModal(i)}>✏️</Button>
                    {i.statut !== 'en_cours' && (
                      <Button size="sm" variant="outline-danger"
                        onClick={() => { setInterventionToDelete(i); setShowConfirm(true); }}>
                        🗑️
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Modal création / édition */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editIntervention ? '✏️ Modifier l\'intervention' : '📅 Nouvelle Intervention'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}

            <div className="row">
              <div className="col-md-8">
                <Form.Group className="mb-3">
                  <Form.Label>Titre *</Form.Label>
                  <Form.Control type="text" name="titre" required
                    value={form.titre} onChange={handleField} />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Type</Form.Label>
                  <Form.Select name="type" value={form.type} onChange={handleField}>
                    {TYPES.map(t => (
                      <option key={t} value={t}>
                        {TYPE_BADGE[t].icon} {TYPE_BADGE[t].label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Chantier *</Form.Label>
                  <Form.Select name="chantier_id" required
                    value={form.chantier_id} onChange={handleField}
                    disabled={!!editIntervention}>
                    <option value="">-- Sélectionner --</option>
                    {chantiers.map(c => (
                      <option key={c.id} value={c.id}>{c.nom}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Responsable</Form.Label>
                  <Form.Select name="user_id" value={form.user_id} onChange={handleField}>
                    <option value="">-- Non assigné --</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>
                        {u.username} ({u.role})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Date et heure de début *</Form.Label>
                  <Form.Control type="datetime-local" name="date_debut" required
                    value={form.date_debut} onChange={handleField} />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Date et heure de fin</Form.Label>
                  <Form.Control type="datetime-local" name="date_fin"
                    value={form.date_fin} onChange={handleField} />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Statut</Form.Label>
                  <Form.Select name="statut" value={form.statut} onChange={handleField}>
                    {STATUTS.map(s => (
                      <option key={s} value={s}>{STATUT_BADGE[s].label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Lieu</Form.Label>
                  <Form.Control type="text" name="lieu"
                    placeholder="ex: Bâtiment A, 2ème étage"
                    value={form.lieu} onChange={handleField} />
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows={2} name="description"
                value={form.description} onChange={handleField} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control as="textarea" rows={2} name="notes"
                value={form.notes} onChange={handleField} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Annuler</Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting
                ? (editIntervention ? 'Mise à jour...' : 'Création...')
                : (editIntervention ? 'Mettre à jour' : 'Créer')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal confirmation suppression */}
      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>⚠️ Confirmer la suppression</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Supprimer l'intervention <strong>{interventionToDelete?.titre}</strong> ?
          <Alert variant="warning" className="mt-3 mb-0">
            Les interventions en cours ne peuvent pas être supprimées.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirm(false)}>Annuler</Button>
          <Button variant="danger" onClick={handleDelete}>🗑️ Supprimer</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default InterventionsList;